// Tab "Solicitar ahora" de Informes en Monitor.
//
// Replica lo que hace InformesTab.qml en tc_qml: rango de fechas, tipos
// de informes y destinatarios verificados. Click "Enviar Informes" →
// POST /monitor/reports-send-now (fire-and-forget) → toast "En cola" +
// seguimiento del job en vivo via subcollection users/{key}/report_jobs.
//
// Escuchamos la subcollection con onSnapshot local. Se usa subcollection
// (no map field) para habilitar Firestore TTL: cada doc tiene `delete_at`
// a 30 días y Firebase auto-borra jobs viejos.

import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, orderBy, limit } from "firebase/firestore";
import { firestore } from "../../firebase";
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";
import "moment/locale/es";
import { auth } from "../../firebase";

const TCSOCKET_URL =
  process.env.REACT_APP_TCSOCKET_URL || "https://api.ticketcontrol.net";

// Tipos disponibles para solicitud manual. Estos son los keys que espera
// el slot sendInformes en tc_qml (VALID_REPORT_TYPES en reports_validator.py).
const AVAILABLE_TYPES = [
  { key: "resumen", label: "Resumen General" },
  { key: "cash_movements", label: "Movimientos de Caja" },
  { key: "closed_tickets", label: "Tickets Cerrados" },
  { key: "mensuales", label: "Abonados" },
  { key: "inout", label: "Entradas / Salidas" },
  { key: "invoices", label: "Facturas" },
  { key: "deals", label: "Acuerdos" },
  { key: "deals_cc", label: "Acuerdos - Cuentas" },
];

// Formato de fecha para el input type="date"
const todayISO = () => moment().format("YYYY-MM-DD");

const SolicitarTab = ({ machineKey, reportEmails, canEdit }) => {
  const verifiedEmails = useMemo(
    () => (reportEmails || []).filter((e) => e.verified),
    [reportEmails]
  );

  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sending, setSending] = useState(false);

  // Lista de jobs recientes leída en vivo desde la subcollection.
  // Limitamos a los últimos 20 ordenados por created_at descendente.
  const [reportJobs, setReportJobs] = useState([]);
  useEffect(() => {
    if (!machineKey) return;
    const jobsCol = collection(firestore, "users", machineKey, "report_jobs");
    // orderBy created_at desc; fallback a sort manual en el useMemo si
    // algún doc no tiene created_at (legacy o fallo al escribir queued).
    const q = query(jobsCol, orderBy("created_at", "desc"), limit(20));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = snap.docs.map((d) => ({ jobId: d.id, ...d.data() }));
        setReportJobs(arr);
      },
      (err) => {
        console.warn("[SolicitarTab] report_jobs onSnapshot error:", err);
        // Fallback sin orderBy si falta el índice o el campo
        const fallbackUnsub = onSnapshot(
          jobsCol,
          (snap) => {
            const arr = snap.docs.map((d) => ({ jobId: d.id, ...d.data() }));
            setReportJobs(arr);
          }
        );
        return () => fallbackUnsub();
      }
    );
    return () => unsub();
  }, [machineKey]);

  const toggleType = (key) => {
    setSelectedTypes((arr) =>
      arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key]
    );
  };

  const toggleRecipient = (email) => {
    setSelectedRecipients((arr) =>
      arr.includes(email) ? arr.filter((x) => x !== email) : [...arr, email]
    );
  };

  const canSend =
    canEdit &&
    !sending &&
    selectedTypes.length > 0 &&
    selectedRecipients.length > 0 &&
    dateFrom && dateTo && dateFrom <= dateTo;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Sesión expirada. Volvé a iniciar sesión.");
        setSending(false);
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch(`${TCSOCKET_URL}/monitor/reports-send-now`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: machineKey,
          payload: {
            dateFrom,
            dateTo,
            reportTypes: selectedTypes,
            recipients: selectedRecipients,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || "No se pudo encolar el envío.");
        if (Array.isArray(data.errors) && data.errors.length) {
          console.warn("[SolicitarTab] errors:", data.errors);
        }
        setSending(false);
        return;
      }
      toast.success(
        `Informes en cola. La máquina los está generando y enviando.`,
        { autoClose: 5000 }
      );
      setSelectedTypes([]);
      setSelectedRecipients([]);
    } catch (err) {
      toast.error(`Error de red: ${err.message || err}`);
    } finally {
      setSending(false);
    }
  };

  // ── Jobs recientes ──
  // Ya vienen ordenados por created_at desc desde Firestore (ver useEffect),
  // pero reordenamos defensivamente + limitamos a 8 para el card.
  const recentJobs = useMemo(() => {
    const arr = [...reportJobs];
    arr.sort((a, b) => {
      const ta = a.finished_at || a.started_at || a.created_at || "";
      const tb = b.finished_at || b.started_at || b.created_at || "";
      return String(tb).localeCompare(String(ta));
    });
    return arr.slice(0, 8);
  }, [reportJobs]);

  return (
    <>
      <Row>
        <Col md={7}>
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-3">Seleccioná las fechas</h6>
              <Row className="g-3 mb-4">
                <Col md={6}>
                  <label className="small text-muted mb-1">Desde</label>
                  <Input
                    type="date"
                    value={dateFrom}
                    max={todayISO()}
                    onChange={(e) => setDateFrom(e.target.value)}
                    disabled={!canEdit}
                  />
                </Col>
                <Col md={6}>
                  <label className="small text-muted mb-1">Hasta</label>
                  <Input
                    type="date"
                    value={dateTo}
                    max={todayISO()}
                    onChange={(e) => setDateTo(e.target.value)}
                    disabled={!canEdit}
                  />
                </Col>
              </Row>

              <h6 className="mb-2">Seleccioná los destinatarios</h6>
              {verifiedEmails.length === 0 ? (
                <div className="small text-muted fst-italic mb-2">
                  No hay emails verificados. Configurá y verificá emails en la pestaña "Configuración".
                </div>
              ) : (
                <div className="d-flex flex-column gap-2 mb-2">
                  {verifiedEmails.map((e) => {
                    const selected = selectedRecipients.includes(e.email.toLowerCase());
                    return (
                      <div
                        key={e.id}
                        className="d-flex align-items-center gap-2"
                        style={{ cursor: canEdit ? "pointer" : "default" }}
                        onClick={() => canEdit && toggleRecipient(e.email.toLowerCase())}
                      >
                        <Input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {}}
                          disabled={!canEdit}
                        />
                        <div>
                          <span>{e.alias || e.email}</span>
                          {e.alias && (
                            <span className="text-muted small ms-2">({e.email})</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md={5}>
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-3">Seleccioná los informes</h6>
              <div className="d-flex flex-column gap-2">
                {AVAILABLE_TYPES.map((t) => {
                  const selected = selectedTypes.includes(t.key);
                  return (
                    <div
                      key={t.key}
                      className="d-flex align-items-center gap-2"
                      style={{ cursor: canEdit ? "pointer" : "default" }}
                      onClick={() => canEdit && toggleType(t.key)}
                    >
                      <Input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {}}
                        disabled={!canEdit}
                      />
                      <span>{t.label}</span>
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <div className="text-center my-3">
        <Button color="primary" size="lg" disabled={!canSend} onClick={handleSend}>
          {sending ? <Spinner size="sm" /> : (
            <>
              <span className="material-symbols-rounded align-middle me-1" style={{ fontSize: 18 }}>
                send
              </span>
              Enviar Informes
            </>
          )}
        </Button>
      </div>

      {/* Historial de jobs */}
      {recentJobs.length > 0 && (
        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-3">
              <span className="material-symbols-rounded align-middle me-1" style={{ fontSize: 18 }}>
                history
              </span>
              Envíos recientes
            </h6>
            <div className="d-flex flex-column gap-2">
              {recentJobs.map((job) => (
                <JobRow key={job.jobId} job={job} />
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────
// Row de un job de envío: estado, timestamp, detalle
// ─────────────────────────────────────────────────────────
const JobRow = ({ job }) => {
  const statusColor = {
    queued: "secondary",
    running: "info",
    done: "success",
    error: "danger",
  }[job.status] || "secondary";

  const statusLabel = {
    queued: "En cola",
    running: "Generando",
    done: "Enviado",
    error: "Error",
  }[job.status] || job.status;

  const when = job.finished_at || job.started_at || job.created_at;
  const whenMoment = when ? moment(when) : null;

  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 8,
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <Badge color={statusColor} pill>
        {job.status === "running" && <Spinner size="sm" className="me-1" />}
        {statusLabel}
      </Badge>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="small" style={{ fontWeight: 500 }}>
          {(job.report_types || []).join(", ") || "Envío de informes"}
        </div>
        <div className="text-muted" style={{ fontSize: 11 }}>
          {job.requested_by && <span>{job.requested_by} · </span>}
          {job.recipients_count != null && <span>{job.recipients_count} destinatario(s) · </span>}
          {whenMoment && <span>{whenMoment.locale("es").fromNow()}</span>}
        </div>
        {job.status === "done" && job.sent_count != null && (
          <div className="text-success small">
            {job.sent_count} email(s) enviado(s)
          </div>
        )}
        {job.status === "error" && (
          <div className="text-danger small">{job.error || "Error desconocido"}</div>
        )}
      </div>
    </div>
  );
};

export default SolicitarTab;
